import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Influencer, InfluencerCall, CallEdit } from "@/types/influencer";
import { groupCallsIntoPositions } from "@/utils/positions";
import { useClassificationProgress } from "@/hooks/useClassificationProgress";

export function useInfluencerByHandle(handle: string | undefined) {
  const normalizedHandle = handle?.replace(/^@/, "").toLowerCase() ?? "";
  const { remaining } = useClassificationProgress(normalizedHandle);

  return useQuery({
    queryKey: ["influencer", normalizedHandle],
    enabled: !!normalizedHandle,
    queryFn: async (): Promise<Influencer | null> => {
      // Fetch the specific user
      // Handles in DB are stored with @ prefix
      const { data: users, error: userErr } = await supabase
        .from("x_users")
        .select("*")
        .or(`handle.ilike.${normalizedHandle},handle.ilike.@${normalizedHandle}`)
        .limit(1);
      if (userErr) throw userErr;
      if (!users || users.length === 0) return null;
      const user = users[0];

      // Fetch calls for this user only (paginated)
      const pageSize = 1000;
      const allCalls: any[] = [];
      let from = 0;
      while (true) {
        const { data: page, error: callErr } = await supabase
          .from("x_calls")
          .select("*")
          .eq("x_user_id", user.x_user_id)
          .order("tweet_date", { ascending: false })
          .range(from, from + pageSize - 1);
        if (callErr) throw callErr;
        if (!page || page.length === 0) break;
        allCalls.push(...page);
        if (page.length < pageSize) break;
        from += pageSize;
      }

      // Fetch asset prices for assets this user has calls on
      const uniqueAssets = [...new Set(allCalls.map((c) => c.asset))];
      const priceMap: Record<string, number> = {};
      if (uniqueAssets.length > 0) {
        // Batch fetch in chunks of 100
        for (let i = 0; i < uniqueAssets.length; i += 100) {
          const chunk = uniqueAssets.slice(i, i + 100);
          const { data: prices } = await supabase
            .from("asset_prices")
            .select("*")
            .in("ticker", chunk);
          if (prices) {
            for (const p of prices) {
              priceMap[p.ticker] = Number(p.current_price);
            }
          }
        }
      }

      // Fetch edits for all calls
      const callIds = allCalls.map((c) => c.id);
      const editMap: Record<string, CallEdit[]> = {};
      if (callIds.length > 0) {
        for (let i = 0; i < callIds.length; i += 100) {
          const chunk = callIds.slice(i, i + 100);
          const { data: edits } = await supabase
            .from("x_call_edits")
            .select("*")
            .in("call_id", chunk)
            .order("created_at", { ascending: true });
          if (edits) {
            for (const e of edits) {
              const mapped: CallEdit = {
                id: e.id,
                callId: e.call_id,
                oldAction: e.old_action,
                newAction: e.new_action,
                editType: e.edit_type as CallEdit["editType"],
                comment: e.comment,
                editedBy: e.edited_by,
                editorEmail: e.editor_email,
                createdAt: e.created_at,
              };
              if (!editMap[e.call_id]) editMap[e.call_id] = [];
              editMap[e.call_id].push(mapped);
            }
          }
        }
      }

      const mappedCalls: InfluencerCall[] = allCalls.map((c) => ({
        id: c.id,
        date: c.tweet_date || c.created_at,
        asset: c.asset,
        action: c.action as InfluencerCall["action"],
        priceAtCall: c.price_at_call ? Number(c.price_at_call) : null,
        priceSourceInterval: c.price_source_interval ?? null,
        priceSourceTimestamp: c.price_source_timestamp ?? null,
        currentPrice: priceMap[c.asset] ?? null,
        pnlPercent: null,
        tweetSnippet: c.tweet_text || "",
        tweetId: c.tweet_id,
        mediaUrl: c.media_url ?? null,
        edits: editMap[c.id] || [],
      }));

      const positions = groupCallsIntoPositions(mappedCalls);
      const allWithPnl = positions.filter((p) => p.pnlPercent !== null);
      const winRate = allWithPnl.length > 0
        ? Math.round((allWithPnl.filter((p) => (p.pnlPercent ?? 0) >= 0).length / allWithPnl.length) * 100)
        : 0;
      const avgPnl = allWithPnl.length > 0
        ? allWithPnl.reduce((sum, p) => sum + (p.pnlPercent ?? 0), 0) / allWithPnl.length
        : 0;
      const bestCall = allWithPnl.length > 0 ? Math.max(...allWithPnl.map((p) => p.pnlPercent ?? 0)) : 0;
      const worstCall = allWithPnl.length > 0 ? Math.min(...allWithPnl.map((p) => p.pnlPercent ?? 0)) : 0;
      const withPnlPerDay = positions.filter((p) => p.pnlPerDay !== null);
      const avgPnlPerDay = withPnlPerDay.length > 0
        ? withPnlPerDay.reduce((sum, p) => sum + (p.pnlPerDay ?? 0), 0) / withPnlPerDay.length
        : 0;

      return {
        id: user.x_user_id,
        handle: user.handle,
        displayName: user.display_name || user.handle,
        avatar: user.profile_image_url || `https://api.dicebear.com/9.x/bottts/svg?seed=${user.x_user_id}`,
        followers: "",
        totalCalls: positions.filter((p) => p.action !== "WATCH").length,
        winRate,
        avgPnl,
        bestCall,
        worstCall,
        avgPnlPerDay,
        claimed: user.claimed ?? false,
        calls: mappedCalls,
      };
    },
    refetchInterval: remaining && remaining > 0 ? 10_000 : false,
  });
}
